/**
 * Staff (03 §4.4, §4.6). A staff account is global (email unique); `venue_membership` gives it a role at
 * a venue; `platform_role = helix_admin` gives Helix operators every venue. A session has one active
 * venue, switchable among the venues the account can reach.
 */
import { z } from 'zod';
import { Email, Id, Instant, PhoneE164, Slug, Timestamps } from './common.js';
/** Role at a venue. `viewer` is read-only; `staff-users` is owner-only. */
export const StaffRole = z.enum(['owner', 'coordinator', 'viewer']);
/** Platform-wide role. `helix_admin` reaches every venue with owner rights. */
export const PlatformRole = z.enum(['helix_admin']);
export const NotifyPrefs = z.object({
    briefings: z.array(z.enum(['sms', 'push', 'email'])),
    needsYou: z.array(z.enum(['sms', 'push', 'email'])),
});
/** The global account. */
export const StaffAccount = z.object({
    id: Id,
    name: z.string(),
    email: Email,
    phone: PhoneE164.nullable(),
    phoneVerifiedAt: Instant.nullable(),
    platformRole: PlatformRole.nullable(),
    notifyPrefs: NotifyPrefs,
    lastSignInAt: Instant.nullable(),
    ...Timestamps,
});
/** A staff member as one venue sees them (`/s/staff-users`): the account plus their membership there. */
export const StaffUser = StaffAccount.omit({ platformRole: true }).extend({
    role: StaffRole,
    /** Membership active at this venue. */
    active: z.boolean(),
});
/** Creates the account if the email is new, then the membership at the active venue. */
export const StaffUserInput = z.object({
    name: z.string().trim().min(1).max(120),
    email: Email,
    phone: PhoneE164.nullable(),
    role: StaffRole,
    active: z.boolean(),
    notifyPrefs: NotifyPrefs,
});
/** A venue the account can switch to, and the role it has there. */
export const VenueAccess = z.object({
    venueId: Id,
    slug: Slug,
    name: z.string(),
    role: StaffRole,
    /** `platform` = reached through `helix_admin`, not a membership. */
    via: z.enum(['membership', 'platform']),
});
/** Cookie names and the CSRF header (08 §5). */
export const STAFF_SESSION_COOKIE = 'becca_staff';
export const STAFF_CSRF_COOKIE = 'becca_csrf';
export const STAFF_CSRF_HEADER = 'x-becca-csrf';
/** Guest requests from the WordPress embed send the venue's `embed_key` in this header. */
export const VENUE_HEADER = 'x-becca-venue';
export const AuditAction = z.enum([
    'create',
    'update',
    'delete',
    'version',
    'sign_in',
    'sign_out',
    'switch_venue',
    'other',
]);
/** Who changed what (Daniel's "who changed the price"). `before`/`after` never hold secrets. */
export const AuditEntry = z.object({
    id: Id,
    actorStaffId: Id.nullable(),
    /** Display name at the time, or `system`. */
    actorLabel: z.string(),
    action: AuditAction,
    /** Table name, e.g. `package`. */
    entity: z.string(),
    entityId: Id.nullable(),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    ...Timestamps,
});
//# sourceMappingURL=staff.js.map